import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { aiAgentService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Bot, Play, Loader2, Lightbulb, ShieldCheck, Settings2, Lock, ArrowRight, Activity,
} from "lucide-react";
import {
  AgentIcon, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, fmtDate,
} from "./shared";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="text-2xl font-bold text-foreground mt-2">{value}</p>
    </div>
  );
}

function AgentCard({ agent, onRun, running, onToggle }) {
  const s = agent.stats || {};
  const placeholder = agent.isPlaceholder;
  return (
    <div className={`bg-card border rounded-xl p-5 flex flex-col gap-3 transition-colors ${placeholder ? "border-border opacity-70" : "border-border hover:border-primary/40"}`} data-testid={`agent-card-${agent.agentCode}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <AgentIcon code={agent.agentCode} className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground truncate">{agent.name}</h3>
            <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{agent.mode === "recommend_only" ? "Recommend-only" : agent.mode}</span>
          </div>
        </div>
        {placeholder ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-border text-muted-foreground bg-muted/40"><Lock className="h-3 w-3" /> Soon</span>
        ) : (
          <button onClick={() => onToggle(agent)} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${agent.enabled ? "text-emerald-600 bg-emerald-500/10 border-emerald-500/30" : "text-muted-foreground bg-muted/40 border-border"}`} data-testid={`agent-toggle-${agent.agentCode}`}>
            {agent.enabled ? "Enabled" : "Disabled"}
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 min-h-[3rem]">{agent.description}</p>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-muted/40 rounded-lg py-1.5"><p className="text-sm font-bold text-foreground">{s.runs || 0}</p><p className="text-[10px] text-muted-foreground">Runs</p></div>
        <div className="bg-muted/40 rounded-lg py-1.5"><p className="text-sm font-bold text-foreground">{s.recommendations || 0}</p><p className="text-[10px] text-muted-foreground">Recs</p></div>
        <div className="bg-muted/40 rounded-lg py-1.5"><p className="text-sm font-bold text-foreground">{s.actionsExecuted || 0}</p><p className="text-[10px] text-muted-foreground">Actions</p></div>
      </div>
      <p className="text-[11px] text-muted-foreground">Last run: {fmtDate(agent.triggers?.schedule?.lastRunAt)}</p>

      <div className="flex items-center gap-2 mt-auto pt-1">
        <button onClick={() => onRun(agent)} disabled={placeholder || running === agent.agentCode}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
          data-testid={`agent-run-${agent.agentCode}`}>
          {running === agent.agentCode ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Run now
        </button>
        <Link to={`/admin/agents/${agent.agentCode}`} className="inline-flex items-center justify-center gap-1 border border-border rounded-lg px-3 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`agent-detail-${agent.agentCode}`}>
          <Settings2 className="h-3.5 w-3.5" /> Manage
        </Link>
      </div>
    </div>
  );
}

export default function AgentCenterPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [center, setCenter] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(null);

  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setCenter(null); return; }
    setLoading(true);
    Promise.all([aiAgentService.center(params), aiAgentService.dashboard(params)])
      .then(([c, d]) => { setCenter(c); setDash(d); })
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);

  useEffect(() => { load(); }, [load]);

  const runAgent = async (agent) => {
    setRunning(agent.agentCode);
    try {
      const r = await aiAgentService.run(agent.agentCode, { tenantId: tenantId || undefined });
      toast.success(r.summary || "Agent run complete");
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setRunning(null); }
  };

  const toggle = async (agent) => {
    try {
      await aiAgentService.configure(agent.agentCode, { enabled: !agent.enabled }, params);
      toast.success(`${agent.name} ${agent.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="agent-center-page">
      <PageHeader icon={Bot} title="AI Agent Center"
        subtitle="Controlled, recommend-first agents — every action is explainable and human-approved.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Recommendations" value={dash?.totalRecommendations ?? "—"} icon={Lightbulb} testid="stat-total-recs" />
            <StatCard label="Acceptance rate" value={dash != null ? `${dash.acceptanceRate}%` : "—"} icon={ShieldCheck} testid="stat-accept-rate" />
            <StatCard label="Pending approvals" value={center?.pendingApprovals ?? "—"} icon={Activity} testid="stat-pending-approvals" />
            <StatCard label="Actions executed" value={dash?.actionsExecuted ?? "—"} icon={ArrowRight} testid="stat-actions" />
          </div>

          <div className="flex items-center gap-3 mb-4">
            <Link to="/admin/agents/recommendations" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="link-recommendations">
              <Lightbulb className="h-4 w-4" /> Recommendations
              {center?.pendingRecommendations > 0 && <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-[11px]">{center.pendingRecommendations}</span>}
            </Link>
            <Link to="/admin/agents/approvals" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="link-approvals">
              <ShieldCheck className="h-4 w-4" /> Approvals
              {center?.pendingApprovals > 0 && <span className="bg-amber-500 text-white rounded-full px-1.5 text-[11px]">{center.pendingApprovals}</span>}
            </Link>
          </div>

          {loading && !center ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agent-grid">
              {(center?.agents || []).map((a) => (
                <AgentCard key={a.agentCode} agent={a} onRun={runAgent} running={running} onToggle={toggle} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
